import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import {
	BookingCodeRow,
	NameAndId,
	PassengerInfo,
} from './PassengerBookingInfo.styles';

type THighlightedOrDisabled =
	| {
			highlighted?: boolean;
			/**
			 * @param disabledText can't be provided if there is defined highlighted and vice versa
			 */
			disabledText?: never;
	  }
	| {
			/**
			 * @param highlighted can't be provided if there is defined disabledText and vice versa
			 */
			highlighted?: never;
			disabledText?: boolean;
	  };

type TPassengerBookingInfoProps = THighlightedOrDisabled & {
	bookingId: string;
	additionalPassengersCount: number;
	bookingCode?: string;
	testId?: string;
	hasSeparator?: boolean;
	mainPassengerFullName?: string;
};

export const PassengerBookingInfo: FC<TPassengerBookingInfoProps> = ({
	additionalPassengersCount,
	mainPassengerFullName = '',
	hasSeparator,
	highlighted,
	disabledText,
	bookingId,
	bookingCode,
	testId = 'PassengerBookingInfo',
}) => {
	const { t } = useTranslation('checkIn');

	return (
		<PassengerInfo
			data-testid={testId}
			highlighted={highlighted}
			disabledText={disabledText}
			hasSeparator={hasSeparator}
		>
			<NameAndId>
				<Typography data-testid={`${testId}-PassengerName`} variant="body2">
					{mainPassengerFullName.match(/.{1,24}/g)?.join('\n') ??
						mainPassengerFullName}
				</Typography>
				<Typography data-testid={`${testId}-BookingId`} variant="body2">
					{bookingId}
				</Typography>
			</NameAndId>
			{!!additionalPassengersCount && (
				<div data-testid={`${testId}-MoreAttendees`} id="more-attendees">
					{t('text.another_guest', { count: additionalPassengersCount })}
				</div>
			)}
			{!!bookingCode && (
				<BookingCodeRow>
					<Typography data-testid={`${testId}-BookingCodeLabel`} variant="b2">
						{t('text.booking_code')}
					</Typography>
					<span data-testid={`${testId}-BookingCode`}>{bookingCode}</span>
				</BookingCodeRow>
			)}
		</PassengerInfo>
	);
};
